// Chi tiết thẻ liệu trình từ máy chủ (GET /api/v2/treatment/cards/{id}): số dư, dịch vụ, thanh toán theo đợt, nhật ký.
// Thu đợt sau đã nối API; trừ buổi, đặt lịch, gia hạn, điều chỉnh, hoàn tiền chờ API giai đoạn 2b / 3 nên giữ nút ở trạng thái khoá.
import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Empty, Progress, Spin, Table, Tabs, Timeline, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { CalendarClock, CalendarPlus, CircleDollarSign, PencilRuler, ReceiptText, ScanLine, SlidersHorizontal, Undo2, X } from 'lucide-react';
import { getTreatmentCard, type CardDetailDto, type CardFlagsDto, type CardListItem } from '../../api/TreatmentV2';
import { formatPhone } from '../POS/utils';
import { KIND_LABEL, ROLE_LABEL } from './data';
import { useTreatmentAuth } from './actions';
import { PayInstallmentModal, METHOD_TEXT } from './SellCardApi';
import { CARD_STATUS, INSTALLMENT_STATE, Pill, fmtDate, fmtDateTime, money, moneyShort } from './ui';

const SOURCE_TEXT: Record<string, string> = { pos: 'Bán qua POS', import: 'Chuyển đổi' };
const EVENT_COLOR: Record<string, string> = {
  issue: 'blue', payment: 'green', use: '#3e91ab', cancel_use: 'gray', extend: 'orange', adjust: 'purple', booking: 'cyan', refund: 'red', plan: 'gold', print: 'gray',
};
const NEXT_PHASE = 'Chờ API giai đoạn sau (2b / 3)';

/** Cảnh báo của thẻ theo cờ backend tính */
export function ApiFlagPills({ f }: { f: CardFlagsDto }) {
  const out: React.ReactNode[] = [];
  if (f.overdue) out.push(<Pill key="o" cls="bg-red-600 text-white border-red-600" label={`Quá hạn ${moneyShort(f.overdue_amount)}`} title={`Trễ ${f.max_overdue_days} ngày`} />);
  else if (f.due_soon) out.push(<Pill key="d" cls="bg-amber-50 text-amber-700 border-amber-200" label="Sắp đến hạn đóng" />);
  if (f.low) out.push(<Pill key="l" cls="bg-orange-50 text-orange-700 border-orange-200" label="Sắp hết thẻ" />);
  if (f.expiring) out.push(<Pill key="e" cls="bg-yellow-50 text-yellow-700 border-yellow-200" label={f.days_to_expire <= 0 ? 'Hết hạn hôm nay' : `Hết hạn sau ${f.days_to_expire} ngày`} />);
  if (f.pending_refund) out.push(<Pill key="r" cls="bg-violet-50 text-violet-700 border-violet-200" label="Chờ hoàn tiền" />);
  return out.length ? <span className="inline-flex flex-wrap gap-1">{out}</span> : null;
}

/** Số dư thẻ: thanh tiến độ + "còn x/y buổi" hoặc "còn x / mệnh giá" */
export function ApiBalanceCell({ card, wide }: { card: Pick<CardListItem, 'kind' | 'balance'>; wide?: boolean }) {
  const b = card.balance;
  const pct = Math.round((b.pct_left || 0) * 100);
  const text = card.kind === 'session'
    ? <><b>{b.remaining_sessions ?? 0}</b>/{b.total_sessions ?? 0} buổi</>
    : <><b>{moneyShort(b.remaining_value ?? 0)}</b> / {moneyShort(b.face_value ?? 0)}</>;
  return (
    <div className={wide ? 'w-full' : 'w-36'}>
      <div className="text-xs text-slate-600">Còn {text}</div>
      <Progress percent={pct} showInfo={false} size="small" strokeColor={pct <= 20 ? '#f97316' : '#3e91ab'} className="!m-0" />
    </div>
  );
}

const ActionBtn = ({ icon, label, onClick, primary, disabled, tip }: { icon: React.ReactNode; label: string; onClick?: () => void; primary?: boolean; disabled?: boolean; tip?: string }) => (
  <Tooltip title={tip}>
    <button type="button" onClick={onClick} disabled={disabled}
      className={`flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${primary ? 'bg-[#357f96] text-white hover:bg-[#2c6b7f]' : 'border border-slate-300 bg-white text-slate-700 hover:border-[#3e91ab] hover:text-[#2c6b7f]'}`}>
      {icon}{label}
    </button>
  </Tooltip>
);

export default function CardDetailApi({ cardId, onClose, onChanged }: { cardId: string | null; onClose: () => void; onChanged?: () => void }) {
  const { can } = useTreatmentAuth();
  const [card, setCard] = useState<CardDetailDto | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [tab, setTab] = useState('lines');

  const load = useCallback(async (id: string) => {
    setLoading(true);
    const r = await getTreatmentCard(id);
    setLoading(false);
    if (r.ok && r.data) { setCard(r.data); setError(''); } else { setCard(null); setError(r.message || 'Không tải được thẻ'); }
  }, []);

  useEffect(() => { setTab('lines'); setCard(null); setError(''); if (cardId) load(cardId); }, [cardId, load]);

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {card ? <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold">{card.customer?.name || 'Khách hàng'}</span>
            {card.customer?.phone && <span className="text-sm font-normal text-slate-500">{formatPhone(card.customer.phone)}</span>}
            <Pill {...CARD_STATUS[card.display_status]} />
            <ApiFlagPills f={card.flags} />
          </div>
          <div className="text-sm font-normal text-slate-600">
            {card.package_name} · <span className="font-mono">{card.card_code}</span> · {KIND_LABEL[card.kind]} · {SOURCE_TEXT[card.source] || card.source}
            {card.order && <> · <Link to={`/pos/orders?id=${card.order.id}&from=2000-01-01`} className="font-mono text-[#2c6b7f] hover:underline">{card.order.order_code}</Link></>}
          </div>
        </> : <span className="text-lg font-bold">Thẻ liệu trình</span>}
      </div>
      <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" aria-label="Đóng"><X className="h-5 w-5" /></button>
    </div>
  );

  if (!card) {
    return (
      <Drawer open={!!cardId} onClose={onClose} width={900} closable={false} destroyOnHidden title={header}>
        {loading ? <div className="flex justify-center py-16"><Spin /></div> : error && <Empty description={error} />}
      </Drawer>
    );
  }

  const active = card.display_status === 'active';
  const debt = card.outstanding;

  const tabs = [
    {
      key: 'lines', label: card.kind === 'session' ? 'Dịch vụ trong thẻ' : 'Số dư thẻ',
      children: card.kind === 'session' ? (
        <Table size="small" rowKey="line_id" pagination={false} dataSource={card.lines} className="custom-ant-table"
          columns={[
            { title: 'Dịch vụ', render: (_, l) => <span>{l.service_name}{l.service_group && <div className="text-xs text-slate-400">{l.service_group}</div>}</span> },
            { title: 'Loại', width: 90, render: (_, l) => <Pill label={ROLE_LABEL[l.role]} cls={l.role === 'bonus' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-600 border-slate-200'} /> },
            { title: 'Theo gói', width: 80, align: 'right', dataIndex: 'total_sessions' },
            { title: 'Đã dùng', width: 80, align: 'right', dataIndex: 'used' },
            { title: 'Hoàn', width: 70, align: 'right', render: (_, l) => l.refunded || '—' },
            { title: 'Đã hẹn', width: 70, align: 'right', render: (_, l) => l.booked || '—' },
            { title: 'Còn lại', width: 80, align: 'right', render: (_, l) => <b className="text-[#2c6b7f]">{Math.max(0, l.available)}</b> },
            { title: 'Giá trị/buổi', width: 110, align: 'right', render: (_, l) => (l.unit_value ? money(l.unit_value) : '—') },
          ]} />
      ) : (
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="space-y-1 rounded-lg bg-slate-50 p-3">
            <div className="flex justify-between"><span>Mệnh giá</span><b>{money(card.balance.face_value ?? 0)}đ</b></div>
            <div className="flex justify-between"><span>Đã dùng</span><span>-{money(card.balance.used_value ?? 0)}đ</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 text-base"><span>Số dư</span><b className="text-[#2c6b7f]">{money(card.balance.remaining_value ?? 0)}đ</b></div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="mb-1 font-medium">Dịch vụ áp dụng</div>
            {card.lines.length ? card.lines.map(l => <div key={l.line_id}>{l.service_name}</div>) : <div className="text-slate-500">Mọi dịch vụ (trừ theo giá niêm yết)</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'pay', label: <span>Thanh toán {card.flags.overdue && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500" />}</span>,
      children: (
        <div className="space-y-4">
          {card.installments.length > 0 && (
            <Table size="small" rowKey="no" pagination={false} dataSource={card.installments} className="custom-ant-table"
              columns={[
                { title: 'Đợt', width: 60, dataIndex: 'no' },
                { title: 'Đến hạn', width: 110, render: (_, r) => fmtDate(r.due_date) },
                { title: 'Phải thu', align: 'right', render: (_, r) => money(r.amount) },
                { title: 'Đã thu', align: 'right', render: (_, r) => money(r.paid) },
                { title: 'Còn thiếu', align: 'right', render: (_, r) => (r.left ? <b className={r.state === 'overdue' ? 'text-red-600' : ''}>{money(r.left)}</b> : '—') },
                { title: 'Trạng thái', width: 170, render: (_, r) => <span className="flex items-center gap-1"><Pill {...INSTALLMENT_STATE[r.state]} />{r.state === 'overdue' && <span className="text-xs text-red-600">{r.overdue_days} ngày</span>}{r.state === 'due_soon' && <span className="text-xs text-amber-700">còn {r.days_left} ngày</span>}</span> },
              ]} />
          )}
          <div>
            <div className="mb-1 font-semibold text-slate-700">Các lần thanh toán</div>
            {card.payments.length ? (
              <Table size="small" rowKey="id" pagination={false} dataSource={[...card.payments].reverse()} className="custom-ant-table"
                columns={[
                  { title: 'Thời gian', width: 140, render: (_, p) => fmtDateTime(p.paid_at) },
                  { title: 'Phương thức', render: (_, p) => <span>{METHOD_TEXT[p.payment_method] || p.payment_method}{p.transaction_code && <span className="ml-1 font-mono text-xs text-slate-400">{p.transaction_code}</span>}</span> },
                  { title: 'Thu ngân', render: (_, p) => p.cashier_name || '—' },
                  { title: 'Số tiền', align: 'right', render: (_, p) => <b className="text-green-700">{money(p.amount)}</b> },
                ]} />
            ) : <div className="text-sm text-slate-500">Chưa thu tiền</div>}
            {card.order?.shared && <p className="mt-1 text-xs text-slate-500">Đơn {card.order.order_code} có thêm hàng khác: tiền đã đóng và từng đợt được chia cho thẻ theo tỷ lệ giá thẻ.</p>}
          </div>
        </div>
      ),
    },
    { key: 'usage', label: 'Sử dụng', children: <Empty description="Trừ buổi sẽ có khi nối API giai đoạn 2b" /> },
    { key: 'bookings', label: 'Lịch hẹn', children: <Empty description="Đặt lịch từ thẻ sẽ có khi nối API giai đoạn 3" /> },
    {
      key: 'history', label: 'Lịch sử giao dịch',
      children: card.events.length ? (
        <Timeline className="!mt-2" items={[...card.events].reverse().map(e => ({
          color: EVENT_COLOR[e.kind] || 'gray',
          children: (
            <div className="text-sm">
              <div className="flex flex-wrap justify-between gap-2"><span>{e.text}</span>{e.amount != null && <b className={e.amount < 0 ? 'text-violet-700' : 'text-green-700'}>{e.amount > 0 ? '+' : ''}{money(e.amount)}đ</b>}</div>
              <div className="text-xs text-slate-400">{fmtDateTime(e.created_at)} · {e.created_by_name || '—'}</div>
            </div>
          ),
        }))} />
      ) : <Empty description="Chưa có giao dịch" />,
    },
  ];

  return (
    <Drawer open={!!cardId} onClose={onClose} width={900} closable={false} destroyOnHidden title={header}>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 p-3 sm:col-span-2"><div className="mb-1 text-xs uppercase text-slate-500">Số dư thẻ</div><ApiBalanceCell card={card} wide /></div>
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs uppercase text-slate-500">Hạn dùng</div>
          <div className={`text-base font-bold ${card.display_status === 'expired' ? 'text-orange-600' : ''}`}>{fmtDate(card.expires_at)}</div>
          <div className="text-xs text-slate-500">{card.flags.days_to_expire >= 0 ? `còn ${card.flags.days_to_expire} ngày` : `quá ${-card.flags.days_to_expire} ngày`}</div>
        </div>
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs uppercase text-slate-500">Đã thu / giá thẻ</div>
          <div className="text-base font-bold">{money(card.paid_amount)} <span className="text-xs font-normal text-slate-500">/ {money(card.price)}</span></div>
          <div className={`text-xs ${debt ? 'font-semibold text-red-600' : 'text-slate-500'}`}>{debt ? `Còn nợ ${money(debt)}đ` : 'Đã thanh toán đủ'}</div>
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Bán {fmtDateTime(card.issued_at)}{card.branch && ` tại ${card.branch.name}`}{card.sold_by_name && ` · thu ngân ${card.sold_by_name}`}{card.consultant && ` · tư vấn ${card.consultant.name}`}
        {card.list_price !== card.price && ` · giá gốc ${money(card.list_price)}đ`} · giá trị đã dùng {money(card.consumed_amount)}đ
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {can('create') && <ActionBtn primary icon={<ScanLine className="h-4 w-4" />} label="Trừ buổi" disabled tip={NEXT_PHASE} />}
        {can('create') && <ActionBtn icon={<CalendarPlus className="h-4 w-4" />} label="Đặt lịch" disabled tip={NEXT_PHASE} />}
        {can('create') && <ActionBtn icon={<CircleDollarSign className="h-4 w-4" />} label="Thu đợt sau" onClick={() => setPaying(true)} disabled={!debt || !card.order || !active}
          tip={!debt ? 'Thẻ đã thanh toán đủ' : 'Thu nợ đơn POS của thẻ – tiền vào sổ quỹ ca'} />}
        {can('edit') && <ActionBtn icon={<CalendarClock className="h-4 w-4" />} label="Gia hạn" disabled tip={NEXT_PHASE} />}
        {can('edit') && <ActionBtn icon={<SlidersHorizontal className="h-4 w-4" />} label="Điều chỉnh" disabled tip={NEXT_PHASE} />}
        {can('edit') && <ActionBtn icon={<PencilRuler className="h-4 w-4" />} label="Lịch thanh toán" disabled tip={NEXT_PHASE} />}
        {can('create') && <ActionBtn icon={<Undo2 className="h-4 w-4" />} label="Hoàn tiền" disabled tip={NEXT_PHASE} />}
        <ActionBtn icon={<ReceiptText className="h-4 w-4" />} label="Lịch sử" onClick={() => setTab('history')} />
      </div>

      <Tabs className="mt-2" activeKey={tab} onChange={setTab} items={tabs} />

      <PayInstallmentModal card={paying ? card : null} onClose={() => setPaying(false)}
        onPaid={() => { if (cardId) load(cardId); onChanged?.(); }} />
    </Drawer>
  );
}
